import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Globe, Clock, Shield, DollarSign, Zap, Users, CheckCircle, Building, Menu, X, ChevronDown, Download, Mail, Phone, FileText, Award, BarChart3, Activity, PieChart } from 'lucide-react';
import SignupModal from '../SignupModal';
import LoginModal from '../LoginModal';
import ActivateCardModal from '../ActivateCardModal';

const InvestorsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showActivateCardModal, setShowActivateCardModal] = useState(false);
  const [signupStep, setSignupStep] = useState('details');
  const [loginStep, setLoginStep] = useState('email');
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'US',
    code: '',
    generatedCode: ''
  });
  const [loginData, setLoginData] = useState({
    email: '',
    phone: '',
    loginMethod: 'email',
    code: '',
    generatedCode: ''
  });
  const [activateData, setActivateData] = useState({
    cardNumber: '',
    pin: '',
    code: '',
    generatedCode: ''
  });

  const countries = [
    { code: 'US', name: 'United States', requiresSSN: true },
    { code: 'CA', name: 'Canada', requiresSSN: false },
    { code: 'UK', name: 'United Kingdom', requiresSSN: false },
    { code: 'DE', name: 'Germany', requiresSSN: false },
    { code: 'FR', name: 'France', requiresSSN: false },
    { code: 'NG', name: 'Nigeria', requiresSSN: false },
    { code: 'GH', name: 'Ghana', requiresSSN: false },
    { code: 'KE', name: 'Kenya', requiresSSN: false },
    { code: 'ZA', name: 'South Africa', requiresSSN: false },
    { code: 'BR', name: 'Brazil', requiresSSN: false },
    { code: 'IN', name: 'India', requiresSSN: false }
  ];

  const menuItems = {
    Services: [
      { name: 'Send Money', path: '/send-money' },
      { name: 'Crypto Cards', path: '/crypto-cards' },
      { name: 'Digital Wallets', path: '/digital-wallets' },
      { name: 'Business Solutions', path: '/business-solutions' }
    ],
    Company: [
      { name: 'About Us', path: '/about-us' },
      { name: 'Careers', path: '/careers' },
      { name: 'Press', path: '/press' },
      { name: 'Investors', path: '/investors' }
    ],
    Support: [
      { name: 'Help Center', path: '/help-center' },
      { name: 'Contact Us', path: '/contact-us' },
      { name: 'API Documentation', path: '/api-documentation' },
      { name: 'Security', path: '/security' }
    ],
    Legal: [
      { name: 'Terms of Service', path: '/terms-of-service' },
      { name: 'Privacy Policy', path: '/privacy-policy' },
      { name: 'Compliance', path: '/compliance' },
      { name: 'Licenses', path: '/licenses' }
    ]
  };

  const toggleSubmenu = (menu) => {
    setExpandedMenu(expandedMenu === menu ? null : menu);
  };

  const generateAuthCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const setShowLandingPage = (value) => {
    console.log('Navigation would occur here:', value);
  };

  const investors = [
    {
      name: "Seeking Seed Investment",
      type: "Current Stage",
      amount: "Target: $500K-$2M",
      stage: "Pre-Seed/Seed",
      year: "2026"
    }
  ];

  const financials = [
    {
      quarter: "Year 1 (Projected)",
      revenue: "$2.5M*",
      growth: "Platform Launch",
      users: "50K*",
      volume: "$75M*"
    },
    {
      quarter: "Year 2 (Projected)",
      revenue: "$8.5M*",
      growth: "+240%*",
      users: "200K*",
      volume: "$280M*"
    },
    {
      quarter: "Year 3 (Projected)",
      revenue: "$24M*",
      growth: "+182%*",
      users: "650K*",
      volume: "$850M*"
    },
    {
      quarter: "Year 5 (Projected)",
      revenue: "$120M*",
      growth: "Target",
      users: "2.8M*",
      volume: "$4.2B*"
    }
  ];

  const reports = [
    {
      title: "Executive Summary & Business Plan",
      date: "2026-02-11",
      type: "Business Plan",
      size: "3.2 MB"
    },
    {
      title: "Market Analysis & Opportunity",
      date: "2026-02-11",
      type: "Market Research",
      size: "2.8 MB"
    },
    {
      title: "Financial Projections (5-Year)",
      date: "2026-02-11",
      type: "Financial Model",
      size: "1.9 MB"
    },
    {
      title: "Product & Technology Roadmap",
      date: "2026-02-11",
      type: "Technical Documentation",
      size: "2.4 MB"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white">
      {/* Header */}
      <header className="bg-purple-900/50 backdrop-blur-sm border-b border-purple-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/zanors-logo-full.png" 
                alt="Zanors Logo" 
                className="h-16 md:h-20 mix-blend-normal" 
                style={{ backgroundColor: 'transparent' }}
              />
            </Link>

            {/* Action Buttons + Hamburger (visible on all screen sizes) */}
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowSignupModal(true)}
                className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Sign Up
              </button>
              <button 
                onClick={() => setShowLoginModal(true)}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Login
              </button>
              <button 
                onClick={() => setShowActivateCardModal(true)}
                className="bg-orange-600 hover:bg-orange-700 px-4 py-2 rounded-lg font-medium text-sm md:text-base transition-colors"
              >
                Activate Card
              </button>
              
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
                aria-label="Menu"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Hamburger Dropdown Menu */}
        {isMenuOpen && (
          <div className="bg-purple-800 border-t border-purple-700 max-h-96 overflow-y-auto">
            <div className="container mx-auto px-4 py-4">
              {Object.entries(menuItems).map(([category, items]) => (
                <div key={category} className="mb-2">
                  <button
                    onClick={() => toggleSubmenu(category)}
                    className="w-full flex items-center justify-between p-3 hover:bg-purple-700 rounded-lg transition-colors"
                  >
                    <span className="font-semibold text-lg">{category}</span>
                    <ChevronDown 
                      className={`w-5 h-5 transition-transform ${
                        expandedMenu === category ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {expandedMenu === category && (
                    <div className="ml-4 mt-2 space-y-1 bg-purple-900/50 rounded-lg p-2">
                      {items.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`block p-3 hover:bg-purple-700 rounded-lg transition-colors ${
                            item.path === '/investors' ? 'text-white font-medium bg-purple-700' : 'text-gray-200 hover:text-white'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Fixed QR Code on Right Center Margin */}
      <div 
        className="fixed right-0 top-1/2 transform -translate-y-1/2 z-40 cursor-pointer"
        onMouseEnter={() => setShowQRCode(true)}
        onMouseLeave={() => setShowQRCode(false)}
      >
        <div className={`transition-all duration-300 ${showQRCode ? 'w-48' : 'w-16'} bg-purple-800 border-l-4 border-green-400 rounded-l-lg shadow-lg`}>
          {!showQRCode ? (
            <div className="p-4">
              <div className="w-8 h-8 bg-white rounded grid grid-cols-2 gap-0.5 p-1">
                <div className="bg-black"></div>
                <div className="bg-white"></div>
                <div className="bg-white"></div>
                <div className="bg-black"></div>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <h3 className="text-sm font-bold mb-2 text-center">Get the ZANORS App</h3>
              <div className="bg-white p-2 rounded mb-2">
                <div className="grid grid-cols-8 gap-0.5">
                  {[...Array(64)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`aspect-square ${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <a 
                  href="https://apps.apple.com/app/globalpay" 
                  className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors"
                >
                  App Store
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.globalpay.app" 
                  className="block text-center bg-gray-900 hover:bg-gray-800 px-2 py-1 rounded text-xs transition-colors"
                >
                  Google Play
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-6xl font-bold mb-6">
              Investment <span className="text-blue-400">Opportunity</span>
            </h1>
            <p className="text-xl text-purple-200 mb-8 max-w-4xl mx-auto">
              Building the next generation of cross-border payments infrastructure. We're democratizing access to global financial services through innovative technology that combines traditional banking with cryptocurrency solutions.
            </p>
            <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-full px-8 py-3 inline-block">
              <span className="text-lg font-bold">Seeking Seed Investment • Pre-Revenue • High Growth Potential</span>
            </div>
          </div>

          {/* Problem & Solution */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">The Opportunity</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-red-900 bg-opacity-40 rounded-lg p-8 backdrop-blur-sm border border-red-600">
                <h3 className="text-2xl font-bold mb-4 text-red-400">The Problem</h3>
                <ul className="space-y-3 text-purple-200">
                  <li>• Traditional remittances cost 6-8% in fees</li>
                  <li>• Cross-border payments take 3-5 business days</li>
                  <li>• 1.4 billion adults globally remain unbanked</li>
                  <li>• Enterprises pay excessive fees for international payroll</li>
                  <li>• Crypto adoption hindered by complexity and lack of infrastructure</li>
                  <li>• Bitcoin ATMs are scarce, expensive, and inconvenient</li>
                  <li>• Small businesses lack affordable payment solutions</li>
                </ul>
              </div>
              <div className="bg-green-900 bg-opacity-40 rounded-lg p-8 backdrop-blur-sm border border-green-600">
                <h3 className="text-2xl font-bold mb-4 text-green-400">Our Solution</h3>
                <ul className="space-y-3 text-purple-200">
                  <li>• Instant global transfers at 0.5-1.5% fees</li>
                  <li>• Real-time settlement in 150+ countries</li>
                  <li>• User-friendly platform requiring no banking infrastructure</li>
                  <li>• Batch payment automation for enterprises</li>
                  <li>• Seamless crypto-to-fiat conversion</li>
                  <li>• Innovative BTC crypto gift cards replacing hard-to-find ATMs</li>
                  <li>• Comprehensive API for business integration</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Market Opportunity */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Market Opportunity</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Globe className="text-blue-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">Global Remittances</h4>
                <div className="text-3xl font-bold text-blue-400 mb-2">$780B</div>
                <p className="text-purple-300 text-sm">Annual market size (World Bank 2024)</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <DollarSign className="text-green-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">Crypto Payments</h4>
                <div className="text-3xl font-bold text-green-400 mb-2">$12T</div>
                <p className="text-purple-300 text-sm">Projected by 2030 (Statista)</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Users className="text-purple-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">Unbanked Adults</h4>
                <div className="text-3xl font-bold text-purple-400 mb-2">1.4B</div>
                <p className="text-purple-300 text-sm">Global potential users</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Building className="text-yellow-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">B2B Payments</h4>
                <div className="text-3xl font-bold text-yellow-400 mb-2">$125T</div>
                <p className="text-purple-300 text-sm">Annual cross-border B2B volume</p>
              </div>
            </div>
          </div>

          {/* Our Platform */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Our Platform & Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Send className="text-green-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">P2P Money Transfers</h4>
                <p className="text-purple-300 text-sm">Send money domestically and internationally with instant settlement and minimal fees. Support for 150+ currencies and cryptocurrency options.</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Shield className="text-blue-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">Crypto Gift Cards</h4>
                <p className="text-purple-300 text-sm">Revolutionary BTC crypto gift cards that provide accessible cryptocurrency entry points, eliminating the need for scarce and expensive Bitcoin ATMs worldwide.</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Users className="text-purple-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">Batch Payments</h4>
                <p className="text-purple-300 text-sm">Automated payroll and bulk payment solutions for enterprises. Process thousands of transactions simultaneously with significant cost savings.</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Zap className="text-yellow-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">API Integration</h4>
                <p className="text-purple-300 text-sm">Developer-friendly RESTful API for seamless platform integration. Accept payments in 150+ currencies with comprehensive documentation.</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <Building className="text-orange-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">Smart Contracts</h4>
                <p className="text-purple-300 text-sm">Custom blockchain solutions including automated escrow, multi-signature wallets, token creation, and DeFi protocol integration.</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600">
                <DollarSign className="text-green-400 mb-4" size={32} />
                <h4 className="text-xl font-bold mb-3">Merchant Services</h4>
                <p className="text-purple-300 text-sm">Accept 50+ cryptocurrencies with instant fiat conversion. Lower fees than traditional credit card processing with no chargeback risk.</p>
              </div>
            </div>
          </div>

          {/* Current Fundraising */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Current Fundraising Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {investors.map((investor, index) => (
                <div key={index} className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600 hover:border-blue-500 transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <Building className="text-blue-400 mr-3" size={24} />
                    <div>
                      <h4 className="text-lg font-bold">{investor.name}</h4>
                      <p className="text-purple-300 text-sm">{investor.type}</p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-2xl font-bold text-green-400">{investor.amount}</div>
                      <p className="text-purple-300 text-sm">{investor.stage} • {investor.year}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-lg p-6 backdrop-blur-sm border border-blue-500 md:col-span-2">
                <h4 className="text-xl font-bold mb-4">Use of Funds</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-purple-300">Product Development</span>
                      <span className="font-bold text-green-400">40%</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-purple-300">Engineering Team</span>
                      <span className="font-bold text-blue-400">25%</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-purple-300">Compliance & Licensing</span>
                      <span className="font-bold text-purple-400">15%</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-purple-300">Marketing & User Acquisition</span>
                      <span className="font-bold text-yellow-400">12%</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-purple-300">Operations & Infrastructure</span>
                      <span className="font-bold text-orange-400">5%</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-purple-300">Legal & Professional Services</span>
                      <span className="font-bold text-red-400">3%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Projections */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Financial Projections (5-Year)</h2>
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600 mb-6">
              <div className="bg-yellow-900 bg-opacity-40 border border-yellow-600 rounded-lg p-4 mb-6">
                <p className="text-yellow-200 text-sm font-semibold">
                  <strong>NOTE:</strong> The following figures represent projected financial performance based on market analysis, comparable companies, and our go-to-market strategy. These are forward-looking estimates and actual results may differ materially. Assumptions available in detailed financial model.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-purple-900 bg-opacity-60">
                    <tr>
                      <th className="p-4 text-left">Period</th>
                      <th className="p-4 text-right">Revenue</th>
                      <th className="p-4 text-right">Growth</th>
                      <th className="p-4 text-right">Users</th>
                      <th className="p-4 text-right">Transaction Volume</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-purple-700">
                    {financials.map((quarter, index) => (
                      <tr key={index} className="hover:bg-purple-700 hover:bg-opacity-40">
                        <td className="p-4 font-bold">{quarter.quarter}</td>
                        <td className="p-4 text-right text-green-400 font-bold">{quarter.revenue}</td>
                        <td className="p-4 text-right text-blue-400 font-bold">{quarter.growth}</td>
                        <td className="p-4 text-right">{quarter.users}</td>
                        <td className="p-4 text-right">{quarter.volume}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-purple-300 text-sm mt-4 italic">* Projected figures based on 1.2% average transaction fee and conservative user acquisition models</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-green-900 to-teal-900 rounded-lg p-6 border border-green-600">
                <h4 className="text-xl font-bold mb-4 text-green-400">Revenue Model</h4>
                <ul className="space-y-2 text-purple-200 text-sm">
                  <li>• Transaction fees: 0.5-1.5% per transfer</li>
                  <li>• Enterprise API subscriptions</li>
                  <li>• Premium features & priority support</li>
                  <li>• Crypto-to-fiat conversion spread</li>
                  <li>• White-label licensing</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-lg p-6 border border-blue-600">
                <h4 className="text-xl font-bold mb-4 text-blue-400">Key Assumptions</h4>
                <ul className="space-y-2 text-purple-200 text-sm">
                  <li>• User acquisition cost: $8-15</li>
                  <li>• Customer lifetime value: $280+</li>
                  <li>• Monthly churn rate: 3-5%</li>
                  <li>• Average transaction size: $350</li>
                  <li>• Transactions per user/month: 4.2</li>
                </ul>
              </div>
              
              <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-lg p-6 border border-purple-600">
                <h4 className="text-xl font-bold mb-4 text-purple-400">Path to Profitability</h4>
                <ul className="space-y-2 text-purple-200 text-sm">
                  <li>• Break-even: Month 18-24</li>
                  <li>• Gross margin target: 75-82%</li>
                  <li>• Operating leverage from scale</li>
                  <li>• Infrastructure costs decrease per user</li>
                  <li>• Network effects drive growth</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Investor Resources */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Investor Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {reports.map((report, index) => (
                <div key={index} className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600 hover:border-green-500 transition-all duration-300">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <FileText className="text-green-400 mr-3" size={24} />
                      <div>
                        <h4 className="text-lg font-bold">{report.title}</h4>
                        <p className="text-purple-300 text-sm">{report.type}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-purple-300">
                      <div>{new Date(report.date).toLocaleDateString()}</div>
                      <div>{report.size}</div>
                    </div>
                  </div>
                  <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center">
                    <Download size={16} className="mr-2" />
                    Download PDF
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Competitive Advantages */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Competitive Advantages</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              
              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600 text-center">
                <Zap className="text-yellow-400 mb-3 mx-auto" size={32} />
                <h4 className="font-bold mb-2">Speed</h4>
                <div className="text-2xl font-bold text-yellow-400">Instant</div>
                <p className="text-purple-300 text-sm">Real-time settlement vs 3-5 days</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600 text-center">
                <DollarSign className="text-green-400 mb-3 mx-auto" size={32} />
                <h4 className="font-bold mb-2">Cost Efficiency</h4>
                <div className="text-2xl font-bold text-green-400">85% Lower</div>
                <p className="text-purple-300 text-sm">Fees vs traditional remittance</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600 text-center">
                <Globe className="text-blue-400 mb-3 mx-auto" size={32} />
                <h4 className="font-bold mb-2">Global Reach</h4>
                <div className="text-2xl font-bold text-blue-400">150+</div>
                <p className="text-purple-300 text-sm">Countries & currencies supported</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600 text-center">
                <Shield className="text-purple-400 mb-3 mx-auto" size={32} />
                <h4 className="font-bold mb-2">Dual Technology</h4>
                <div className="text-2xl font-bold text-purple-400">Hybrid</div>
                <p className="text-purple-300 text-sm">Traditional + crypto infrastructure</p>
              </div>

              <div className="bg-purple-800 bg-opacity-60 rounded-lg p-6 backdrop-blur-sm border border-purple-600 text-center">
                <Award className="text-orange-400 mb-3 mx-auto" size={32} />
                <h4 className="font-bold mb-2">BTC Gift Card Innovation</h4>
                <div className="text-2xl font-bold text-orange-400">ATM Replacement</div>
                <p className="text-purple-300 text-sm">Accessible crypto entry without scarce ATMs</p>
              </div>
            </div>
          </div>

          {/* Team & Vision */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Why Now?</h2>
            <div className="bg-gradient-to-r from-green-900 to-blue-900 rounded-lg p-8 border border-green-500">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-green-400">Market Timing</h3>
                  <ul className="space-y-2 text-purple-200">
                    <li>• Cryptocurrency mainstream adoption accelerating</li>
                    <li>• COVID-19 accelerated digital payment adoption</li>
                    <li>• Remote work driving international payments</li>
                    <li>• Regulatory clarity improving globally</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-blue-400">Technology Maturity</h3>
                  <ul className="space-y-2 text-purple-200">
                    <li>• Blockchain infrastructure now scalable</li>
                    <li>• Layer-2 solutions reduce transaction costs</li>
                    <li>• Stablecoin adoption removes volatility concerns</li>
                    <li>• Open banking APIs enable integration</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4 text-purple-400">Market Gap</h3>
                  <ul className="space-y-2 text-purple-200">
                    <li>• Existing solutions serve either crypto OR traditional</li>
                    <li>• Enterprise tools lack user-friendly interfaces</li>
                    <li>• High fees remain barrier to adoption</li>
                    <li>• Limited support for emerging markets</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Milestones & Roadmap */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Development Roadmap</h2>
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600">
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-32 font-bold text-green-400">Q1-Q2 2026</div>
                  <div className="flex-1">
                    <h4 className="font-bold mb-2">Platform MVP & Beta Launch</h4>
                    <ul className="text-purple-300 text-sm space-y-1">
                      <li>• Core P2P transfer functionality</li>
                      <li>• Initial currency support (10 countries)</li>
                      <li>• Basic crypto integration (BTC, ETH, USDT)</li>
                      <li>• Beta testing with 1,000 users</li>
                      <li>• Launch BTC crypto gift card pilot program</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-32 font-bold text-blue-400">Q3-Q4 2026</div>
                  <div className="flex-1">
                    <h4 className="font-bold mb-2">Public Launch & Enterprise Features</h4>
                    <ul className="text-purple-300 text-sm space-y-1">
                      <li>• Full platform launch in 50+ countries</li>
                      <li>• Batch payment automation</li>
                      <li>• API v1 release for developers</li>
                      <li>• Crypto gift card marketplace nationwide rollout</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-32 font-bold text-purple-400">2027</div>
                  <div className="flex-1">
                    <h4 className="font-bold mb-2">Scale & Advanced Features</h4>
                    <ul className="text-purple-300 text-sm space-y-1">
                      <li>• Expand to 150+ countries</li>
                      <li>• Smart contract platform launch</li>
                      <li>• White-label solutions for businesses</li>
                      <li>• Mobile app with enhanced features</li>
                      <li>• BTC gift card partnerships with major retailers</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-32 font-bold text-yellow-400">2028+</div>
                  <div className="flex-1">
                    <h4 className="font-bold mb-2">Market Leadership & Innovation</h4>
                    <ul className="text-purple-300 text-sm space-y-1">
                      <li>• AI-powered fraud detection</li>
                      <li>• DeFi lending and savings products</li>
                      <li>• Strategic partnerships and acquisitions</li>
                      <li>• Potential Series A fundraise</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Investor Contact */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold text-center mb-12">Investment Inquiries</h2>
            <div className="bg-purple-800 bg-opacity-60 rounded-lg p-8 backdrop-blur-sm border border-purple-600 max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold mb-6">Founder Contact</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-bold text-lg">CEO & Founder</h4>
                      <p className="text-purple-300">Chief Executive Officer</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Mail className="text-yellow-400 mr-2" size={16} />
                        <a href="mailto:investors@zanors.com" className="text-yellow-400 hover:text-yellow-300">
                          investors@zanors.com
                        </a>
                      </div>
                      <div className="flex items-center">
                        <Phone className="text-green-400 mr-2" size={16} />
                        <span className="text-green-400">Available upon request</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-6">Investment Information</h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-bold text-lg">Seed Round Details</h4>
                      <p className="text-purple-300">Currently raising via Y Combinator</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <Mail className="text-yellow-400 mr-2" size={16} />
                        <a href="mailto:fundraising@zanors.com" className="text-yellow-400 hover:text-yellow-300">
                          fundraising@zanors.com
                        </a>
                      </div>
                      <div className="text-purple-300 text-sm mt-4">
                        <p>We're seeking strategic investors who can provide not just capital but also industry expertise, network connections, and operational guidance.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-gradient-to-r from-blue-900 to-purple-900 rounded-lg p-8 border border-blue-500 text-center">
            <h2 className="text-3xl font-bold mb-6">Join Us in Building the Future of Finance</h2>
            <p className="text-xl text-purple-200 mb-8">
              We're revolutionizing cross-border payments and bringing financial services to underserved markets globally. Be part of a high-growth opportunity at the intersection of fintech and cryptocurrency.
            </p>
            <div className="flex justify-center space-x-6">
              <a 
                href="mailto:investors@zanors.com"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-bold transition-all duration-300 transform hover:scale-105 inline-block"
              >
                Schedule a Meeting
              </a>
              <button className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-all duration-300 transform hover:scale-105">
                Download Pitch Deck
              </button>
            </div>
            <p className="text-sm text-blue-200 mt-4">Confidential investor materials • Due diligence data room available</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black bg-opacity-50 mt-16 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-lg mb-4">Services</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Services.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
           <div>
  <h4 className="font-bold text-lg mb-4">Company</h4>
  <ul className="space-y-2 text-purple-300">
    {menuItems.Company.map(item => (
      <li key={item.name}>
        <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
      </li>
    ))}
  </ul>
</div>
            <div>
              <h4 className="font-bold text-lg mb-4">Support</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Support.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">Legal</h4>
              <ul className="space-y-2 text-purple-300">
                {menuItems.Legal.map(item => (
                  <li key={item.name}>
                    <Link to={item.path} className="hover:text-white transition-colors">{item.name}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="border-t border-purple-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex items-center space-x-3 mb-4 md:mb-0">
                <div className="w-14 h-14 rounded-full flex items-center justify-center p-2 border border-purple-600 shadow-lg">
                  <img src="/zanors-icon.png" alt="Zanors Icon" className="w-full h-full object-contain" />
                </div>
                <div>
                  <span className="text-white font-bold">Zanors</span>
                  <p className="text-xs text-purple-400">ZANORS is a financial technology platform. ZANORS is not an FDIC-insured Bank or depositary</p>
                  <p className="text-xs text-purple-400">taking institution. In the USA, banking services for the placement of deposits where the customers'</p>
                  <p className="text-xs text-purple-400">funds may be placed are provided by BankProv, Member FDIC. Deposit insurance coverage only</p>
                  <p className="text-xs text-purple-400">protects against the failure of an FDIC insured depository institution. Crypto assets and</p>
                  <p className="text-xs text-purple-400">non-bank digital wallet are not insured by the FDIC, are not deposits, and may lose value.</p>
                </div>
              </div>
              
              <div className="text-center md:text-right">
                <p className="text-purple-300 text-sm">
                  © 2026 Zanors Inc. All rights reserved.
                </p>
                <p className="text-purple-400 text-xs mt-1">
                  Pre-revenue startup seeking seed investment
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <SignupModal 
        showSignupModal={showSignupModal}
        setShowSignupModal={setShowSignupModal}
        signupStep={signupStep}
        setSignupStep={setSignupStep}
        signupData={signupData}
        setSignupData={setSignupData}
        countries={countries}
        generateAuthCode={generateAuthCode}
        setShowLandingPage={setShowLandingPage}
      />
      <LoginModal 
        showLoginModal={showLoginModal}
        setShowLoginModal={setShowLoginModal}
        loginStep={loginStep}
        setLoginStep={setLoginStep}
        loginData={loginData}
        setLoginData={setLoginData}
        generateAuthCode={generateAuthCode}
        setShowLandingPage={setShowLandingPage}
      />
      <ActivateCardModal 
        showActivateCardModal={showActivateCardModal}
        setShowActivateCardModal={setShowActivateCardModal}
        activateData={activateData}
        setActivateData={setActivateData}
        generateAuthCode={generateAuthCode}
        user={null}
        signupData={signupData}
        loginData={loginData}
        setShowLandingPage={setShowLandingPage}
      />
    </div>
  );
};

export default InvestorsPage;